// src/Pages/AdminUsers.js
import React, { useState, useEffect, useMemo } from 'react';
import { FaEdit, FaTrashAlt, FaSave, FaTimes } from 'react-icons/fa';
import { db } from '../firebase';
import { collection, getDocs, deleteDoc, doc, updateDoc } from 'firebase/firestore';

const PAGE_SIZE = 5;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editingData, setEditingData] = useState({});

  // Fetch users
  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = usersSnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          name: data.fullName || 'N/A',
          email: data.email || 'N/A',
          phone: data.phone || 'N/A',
          status: data.status || 'Inactive',
        };
      });
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDelete = async (userId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this user?');
    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, 'users', userId));
      setUsers((prev) => prev.filter((user) => user.id !== userId));
      alert('User deleted successfully!');
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user. Try again later.');
    }
  };

  const handleEdit = (user) => {
    setEditingUserId(user.id);
    setEditingData({
      name: user.name,
      email: user.email,
      phone: user.phone,
      status: user.status,
    });
  };

  const handleCancel = () => {
    setEditingUserId(null);
    setEditingData({});
  };

  const handleSave = async (userId) => {
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        fullName: editingData.name,
        email: editingData.email,
        phone: editingData.phone,
        status: editingData.status,
      });

      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId ? { ...user, ...editingData } : user
        )
      );

      setEditingUserId(null);
      setEditingData({});
      alert('User updated successfully!');
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user. Try again later.');
    }
  };

  const filteredUsers = useMemo(() => {
    let filtered = users.filter(
      (u) =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase()) ||
        u.phone.toLowerCase().includes(search.toLowerCase())
    );

    if (sortKey) {
      filtered = filtered.sort((a, b) => {
        const aKey = a[sortKey]?.toLowerCase() || '';
        const bKey = b[sortKey]?.toLowerCase() || '';
        if (aKey < bKey) return sortOrder === 'asc' ? -1 : 1;
        if (aKey > bKey) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [users, search, sortKey, sortOrder]);

  const pageCount = Math.ceil(filteredUsers.length / PAGE_SIZE);
  const pagedUsers = filteredUsers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortOrder('asc');
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-8 bg-white rounded-lg shadow-lg mt-8">
      <h2 className="text-3xl font-bold mb-6 text-gray-900">Users Management</h2>

      <div className="mb-4 flex justify-between items-center">
        <input
          type="text"
          placeholder="Search by name, email, or phone..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="w-full max-w-md px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full text-left border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th
                className="cursor-pointer px-6 py-3 font-semibold text-gray-700"
                onClick={() => handleSort('name')}
              >
                Name {sortKey === 'name' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="cursor-pointer px-6 py-3 font-semibold text-gray-700"
                onClick={() => handleSort('email')}
              >
                Email {sortKey === 'email' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="cursor-pointer px-6 py-3 font-semibold text-gray-700"
                onClick={() => handleSort('phone')}
              >
                Phone {sortKey === 'phone' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th
                className="cursor-pointer px-6 py-3 font-semibold text-gray-700"
                onClick={() => handleSort('status')}
              >
                Status {sortKey === 'status' && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
              </th>
              <th className="px-6 py-3 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pagedUsers.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-6 text-gray-500 italic">
                  No users found.
                </td>
              </tr>
            ) : (
              pagedUsers.map(({ id, name, email, phone, status }) => (
                <tr key={id} className="border-b hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4">
                    {editingUserId === id ? (
                      <input
                        value={editingData.name}
                        onChange={(e) =>
                          setEditingData((prev) => ({ ...prev, name: e.target.value }))
                        }
                        className="border px-2 py-1 rounded w-full"
                      />
                    ) : (
                      name
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingUserId === id ? (
                      <input
                        value={editingData.email}
                        onChange={(e) =>
                          setEditingData((prev) => ({ ...prev, email: e.target.value }))
                        }
                        className="border px-2 py-1 rounded w-full"
                      />
                    ) : (
                      email
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingUserId === id ? (
                      <input
                        value={editingData.phone}
                        onChange={(e) =>
                          setEditingData((prev) => ({ ...prev, phone: e.target.value }))
                        }
                        className="border px-2 py-1 rounded w-full"
                      />
                    ) : (
                      phone
                    )}
                  </td>
                  <td className="px-6 py-4">
                    {editingUserId === id ? (
                      <select
                        value={editingData.status}
                        onChange={(e) =>
                          setEditingData((prev) => ({ ...prev, status: e.target.value }))
                        }
                        className="border px-2 py-1 rounded w-full"
                      >
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    ) : (
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {status}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 flex gap-2">
                    {editingUserId === id ? (
                      <>
                        <button
                          title="Save"
                          className="text-green-600 hover:text-green-800"
                          onClick={() => handleSave(id)}
                        >
                          <FaSave />
                        </button>
                        <button
                          title="Cancel"
                          className="text-gray-500 hover:text-gray-700"
                          onClick={handleCancel}
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          title="Edit User"
                          className="text-indigo-600 hover:text-indigo-800"
                          onClick={() => handleEdit({ id, name, email, phone, status })}
                        >
                          <FaEdit />
                        </button>
                        <button
                          title="Delete User"
                          className="text-red-600 hover:text-red-800"
                          onClick={() => handleDelete(id)}
                        >
                          <FaTrashAlt />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && (
        <div className="mt-6 flex justify-center space-x-3">
          {[...Array(pageCount)].map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`px-4 py-2 rounded ${
                currentPage === idx + 1
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
